import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSimulator } from "@/hooks/useSimulator";
import { supabase } from "@/integrations/supabase/client";
import PersonaSelect from "@/components/simulator/PersonaSelect";
import SimDashboard from "@/components/simulator/SimDashboard";
import RoundSummary from "@/components/simulator/RoundSummary";
import CompletionScreen from "@/components/simulator/CompletionScreen";
import SimTutorial from "@/components/simulator/SimTutorial";
import SimulatorGate from "@/components/modules/SimulatorGate";
import LunimarScene from "@/components/lunimar/LunimarScene";

const InvestingSimulatorContent = () => {
  const navigate = useNavigate();
  const sim = useSimulator();
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const tutorialKey = `sim_tutorial_${session.user.id}`;
      const tutorialDone = localStorage.getItem(tutorialKey);
      if (!tutorialDone) {
        setShowTutorial(true);
      } else {
        sim.checkExisting();
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, []);

  if (showTutorial) {
    return (
      <SimTutorial
        onComplete={async () => {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            localStorage.setItem(`sim_tutorial_${session.user.id}`, "true");
          }
          setShowTutorial(false);
          sim.checkExisting();
        }}
      />
    );
  }

  if (sim.phase === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <p className="text-muted-foreground font-heading">Loading simulator...</p>
        </div>
      </div>
    );
  }

  if (sim.phase === "setup") {
    return (
      <PersonaSelect
        onStart={sim.startSimulation}
        loading={sim.actionLoading}
        onBack={() => navigate("/")}
      />
    );
  }

  if (sim.phase === "round_summary" && sim.lastRoundData) {
    return (
      <RoundSummary
        data={sim.lastRoundData}
        stocks={sim.stocks}
        onContinue={sim.dismissRoundSummary}
      />
    );
  }

  if (sim.phase === "completed" && sim.simulation) {
    return (
      <CompletionScreen
        simulation={sim.simulation}
        stocks={sim.stocks}
        onTransfer={sim.completeAndTransfer}
        onBack={() => navigate("/finances")}
      />
    );
  }

  if (sim.phase === "playing" && sim.simulation) {
    return (
      <SimDashboard
        simulation={sim.simulation}
        stocks={sim.stocks}
        portfolioValue={sim.portfolioValue}
        netWorth={sim.netWorth}
        currentNews={sim.currentNews}
        onBuy={sim.buyStock}
        onSell={sim.sellStock}
        onAdvance={sim.advanceRound}
        actionLoading={sim.actionLoading}
        error={sim.error}
        onBack={() => navigate("/")}
      />
    );
  }

  return null;
};

const InvestingSimulator = () => (
  <SimulatorGate moduleKey="market_simulator">
    <LunimarScene realmKey="investing">
      <InvestingSimulatorContent />
    </LunimarScene>
  </SimulatorGate>
);

export default InvestingSimulator;
